import {useCallback, useState} from 'react';
import moment, {Moment} from 'moment';
import {useAllSearchParams} from '../../../../hooks/use-search-params';
import {
  calcEndDateByRange,
  calcStartDateByRange,
  DateRangeType,
} from '../../../date-range-input/date-range-utils';
import {CareServicesURLParams, CareServicesViewMode} from './care-service-item';
import {LocalStorageKey} from '../../../../enums';
import {useHistory} from 'react-router-dom';

export function useCareServicesDates(viewMode: CareServicesViewMode) {
  const searchParams = useAllSearchParams<CareServicesURLParams>();
  const history = useHistory();
  const [rangeMode, setRangeMode] = useState<DateRangeType>(() =>
    getInitialRangeMode(searchParams, viewMode)
  );
  const [startDate, setStartDate] = useState<Moment>(() =>
    calcStartDateByRange(rangeMode, moment(searchParams.date))
  );
  const [endDate, setEndDate] = useState<Moment>(() => calcEndDateByRange(rangeMode, startDate));

  const updateSearchOptions = useCallback(
    ({
      startDate,
      endDate,
      range,
    }: {
      endDate?: Moment;
      startDate?: Moment;
      range?: DateRangeType;
    }) => {
      // --- 0. Set new url params ---
      const params = new URLSearchParams(history.location.search);
      if (startDate) {
        params.set('date', startDate.format('YYYY-MM-DD'));
      }
      if (range) {
        params.set('range', range);
      }

      // --- 1. Update url ---
      history.push({search: params.toString()});

      // --- 2. Update state ---
      if (endDate) {
        setEndDate(endDate);
      }
      if (startDate) {
        setStartDate(startDate);
      }
      if (range) {
        setRangeMode(range);
      }

      // --- 3. Update local storage ---
      if (range) {
        localStorage.setItem(LocalStorageKey.CareServicesRange, range);
      }
    },
    [history]
  );

  return {
    rangeMode,
    setRangeMode,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    updateSearchOptions,
  };
}

function getInitialRangeMode(
  searchParams: CareServicesURLParams,
  viewMode: CareServicesViewMode
): DateRangeType {
  // 1. Use url param if available
  let range: DateRangeType | null = searchParams.range ?? null;

  // Special case: seniors(aka general view) view always uses '1w' as initial range
  if (viewMode === 'seniors') {
    return '1w';
  }

  // 2. Use local storage if applicable
  if (!range) {
    range = localStorage.getItem(LocalStorageKey.CareServicesRange) as DateRangeType | null;
  }

  // 3. Validate and return
  switch (range) {
    // Only allow specific values
    case '1d':
    case '1w':
    case '1m':
      return range;
    // Fallback
    default:
      return '1w';
  }
}
